/**
 * Import, Locations Needing Review, Calendar, More, Profile, Help.
 *
 * §25 Import: upload or paste, auto-detect fields, change mapping, row count,
 * ready / needs-review / shared-address counts, Continue.
 * §20 Review: unresolved locations stay visible with Edit Address, Place on Map
 * and Skip for now. Coordinates are never invented.
 * §26 Calendar: Day, Week, Month, Year and Agenda views.
 */

import { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon, ArrowUpTrayIcon, CalendarDaysIcon, ChartBarIcon, ChevronRightIcon,
  ClipboardDocumentCheckIcon, ComputerDesktopIcon, DevicePhoneMobileIcon, FolderOpenIcon, KeyIcon,
  LifebuoyIcon, MapIcon, MapPinIcon, PhoneIcon, RectangleGroupIcon, ShieldCheckIcon,
  UserCircleIcon, UsersIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// Import (§25)
// ---------------------------------------------------------------------------

export interface ImportPreview {
  jobId: string;
  rowCount: number;
  readyCount: number;
  needsReviewCount: number;
  sharedAddressCount: number;
  /** Detected source heading -> canonical field. Operator can change it. §25 */
  mapping: Array<{ sourceHeading: string; field: string; confident: boolean }>;
  availableFields: string[];
  sample: string[][];
}

export function ImportScreen({
  preview, uploading, error, onUpload, onPaste, onMappingChange, onContinue,
}: {
  preview: ImportPreview | null;
  uploading: boolean;
  error: string | null;
  onUpload: (file: File) => void;
  onPaste: (text: string) => void;
  onMappingChange: (sourceHeading: string, field: string) => void;
  onContinue: () => void;
}) {
  const [pasted, setPasted] = useState('');

  return (
    <div className="pt-4">
      <h1 className="mb-4">Import</h1>

      {error && (
        <div role="alert" className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg">
          <p className="text-base text-alert-800">{error}</p>
        </div>
      )}

      {!preview && (
        <>
          <div className="mb-5 rounded-lg border border-neutral-300 bg-neutral-50 p-4">
            <p className="mb-3 text-base text-neutral-800">
              Start with the approved workbook so every column maps correctly.
            </p>
            <a
              className="btn btn-secondary inline-flex items-center gap-2"
              href="/templates/X2F-People-Upload-Template.xlsx"
              download="X2F-People-Upload-Template.xlsx"
            >
              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden />
              Download template
            </a>
          </div>

          <label className="field">
            <span>Upload Excel or CSV</span>
            {/* The accept list is a convenience only. §25 requires MIME
                verification, extension validation, size and decompression
                limits server-side — none of which a browser hint provides. */}
            <input
              type="file"
              className="input"
              accept=".csv,.tsv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUpload(f);
              }}
            />
          </label>

          <p className="text-base text-neutral-700 my-4">or</p>

          <label className="field">
            <span>Paste data</span>
            <textarea
              className="input min-h-[160px] font-mono text-sm"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="Paste rows including the header line"
            />
          </label>

          <button
            className="btn btn-primary"
            onClick={() => onPaste(pasted)}
            disabled={!pasted.trim() || uploading}
          >
            {uploading ? 'Reading…' : 'Read data'}
          </button>
        </>
      )}

      {preview && (
        <>
          <dl className="grid grid-cols-2 desktop:grid-cols-4 gap-4 mb-6">
            <Stat label="Rows" value={preview.rowCount} />
            <Stat label="Ready" value={preview.readyCount} />
            <Stat label="Needs review" value={preview.needsReviewCount}
                  alert={preview.needsReviewCount > 0} />
            <Stat label="Shared addresses" value={preview.sharedAddressCount} />
          </dl>

          <h6 className="text-accent-700 mb-2">Field mapping</h6>
          <ul className="mb-6">
            {preview.mapping.map((m) => (
              <li key={m.sourceHeading}
                  className="border-b border-neutral-300 py-3 flex items-center gap-3">
                <span className="flex-1 min-w-0">
                  <span className="block font-heading text-base truncate">
                    {m.sourceHeading}
                  </span>
                  {/* A low-confidence guess is flagged so the operator checks it
                      rather than trusting auto-detection silently. §25 */}
                  {!m.confident && (
                    <span className="block text-sm text-alert-600">Please confirm</span>
                  )}
                </span>
                <select
                  className="input w-[180px]"
                  value={m.field}
                  aria-label={`Map ${m.sourceHeading}`}
                  onChange={(e) => onMappingChange(m.sourceHeading, e.target.value)}
                >
                  <option value="">Ignore</option>
                  {preview.availableFields.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </li>
            ))}
          </ul>

          <button className="btn btn-primary" onClick={onContinue}>Continue</button>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
  return (
    <div>
      <dt className="text-h6 uppercase text-neutral-700 mb-1">{label}</dt>
      <dd className={`font-heading font-semibold text-h3 tabular-nums ${
        alert ? 'text-alert-600' : ''
      }`}>
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locations Needing Review (§20)
// ---------------------------------------------------------------------------

export interface UnresolvedLocation {
  id: string;
  /** The address exactly as imported. Kept permanently. §20 */
  originalAddress: string;
  city?: string;
  state?: string;
  zip?: string;
  recordSummary: string;
  reason: string;
}

export function LocationsReviewScreen({
  locations, onEditAddress, onPlaceOnMap, onSkip,
}: {
  locations: UnresolvedLocation[];
  onEditAddress: (id: string) => void;
  onPlaceOnMap: (id: string) => void;
  onSkip: (id: string) => void;
}) {
  return (
    <div className="pt-4">
      <h1 className="mb-1">Locations Needing Review</h1>
      <p className="text-base text-neutral-700 mb-6">
        These addresses could not be placed. They stay in the system and are
        excluded from routing until they are resolved.
      </p>

      {locations.length === 0 && (
        <p className="text-base text-neutral-700">Nothing needs review.</p>
      )}

      <ul>
        {locations.map((l) => (
          <li key={l.id} className="border-b border-neutral-300 py-4">
            <p className="font-heading font-semibold text-h4">{l.originalAddress}</p>
            <p className="text-base text-neutral-700">{l.recordSummary}</p>
            <p className="text-base text-alert-600 mt-1">{l.reason}</p>

            <div className="flex flex-wrap gap-2 mt-3">
              <button className="btn btn-secondary" onClick={() => onEditAddress(l.id)}>
                Edit Address
              </button>
              <button className="btn btn-secondary" onClick={() => onPlaceOnMap(l.id)}>
                Place on Map
              </button>
              <button className="btn btn-ghost" onClick={() => onSkip(l.id)}>
                Skip for now
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar (§26)
// ---------------------------------------------------------------------------

export const CALENDAR_VIEWS = ['Day', 'Week', 'Month', 'Year', 'Agenda'] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];

export interface CalendarEventView {
  id: string;
  sourceId?: string;
  routePlanId?: string | null;
  kind: 'team_trip' | 'field_visit' | 'follow_up';
  title: string;
  timeLabel: string;
  localDate: string;
  startMinute: number | null;
  durationMinutes: number | null;
  startsAt: string | null;
  endsAt: string | null;
  teamName: string | null;
  teamId?: string | null;
  personId?: string | null;
  stopCount?: number;
  visitCount?: number;
  recordCount?: number | null;
  roadMiles?: number | null;
  note?: string | null;
  disposition?: string | null;
  hasConflict: boolean;
  conflictLabel: string;
}

export interface CalendarConnectionView {
  id: string;
  provider: 'google' | 'ics';
  providerLabel: string;
  accountLabel: string | null;
  health: 'healthy' | 'attention';
  healthLabel: string;
  lastSuccessfulSyncAt: string | null;
  sharingMode: string;
}

export function CalendarScreen({
  view, selectedDate, events, connections = [], disclosureStatement, timeZone,
  loading = false, error = null, canManageConnections = false,
  onViewChange, onDateSelect, onPrev, onToday, onNext, onSelect, onRetry,
  onManageConnections, now: suppliedNow,
}: {
  view: CalendarView;
  selectedDate: string;
  periodLabel: string;
  events: CalendarEventView[];
  connections?: CalendarConnectionView[];
  disclosureStatement?: string;
  timeZone: string;
  loading?: boolean;
  error?: { message: string; recoverable: boolean } | null;
  canManageConnections?: boolean;
  onViewChange: (v: CalendarView) => void;
  onDateSelect: (date: string, view: CalendarView) => void;
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
  onSelect: (event: CalendarEventView) => void;
  onRetry?: () => void;
  onManageConnections?: () => void;
  now?: Date;
}) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const liveNow = useCalendarNow();
  const currentTime = suppliedNow ?? liveNow;
  const clock = calendarClockSnapshot(currentTime, timeZone);
  const selectedEvent = events.find((event) => eventKey(event) === selectedKey) ?? null;
  const anchor = utcDate(selectedDate);
  const monthYear = anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const title = view === 'Year'
    ? String(anchor.getUTCFullYear())
    : view === 'Day'
      ? anchor.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
      : monthYear;
  const weekStart = new Date(anchor);
  weekStart.setUTCDate(anchor.getUTCDate() - anchor.getUTCDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
  const subtitle = view === 'Day'
    ? anchor.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
    : view === 'Week'
      ? `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })} to ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`
      : '';
  const hasSidebar = view === 'Day' || view === 'Week' || view === 'Month';
  const pickEvent = (event: CalendarEventView) => setSelectedKey(eventKey(event));
  const openEvent = (event: CalendarEventView) => {
    setSelectedKey(eventKey(event));
    onSelect(event);
  };

  return (
    <div className="calendar-shell min-w-0 max-w-[980px] flex flex-col gap-3 pt-3 text-[12px] desktop:pt-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
      <div role="tablist" aria-label="Calendar view" className="flex gap-[2px] rounded-full bg-surface p-[3px] overflow-x-auto scrollbar-none">
        {CALENDAR_VIEWS.map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={view === v}
            onClick={() => onViewChange(v)}
            className={`min-h-[44px] rounded-full border-0 px-3 font-heading text-xs transition-colors desktop:min-h-[32px] desktop:px-[13px] ${view === v ? 'bg-bg text-text shadow-sm' : 'bg-transparent text-neutral-700 hover:text-text'}`}
          >
            {v}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-end gap-[6px]">
        <button className="btn btn-ghost min-h-[44px] w-[44px] p-0 text-lg desktop:min-h-[36px] desktop:w-[36px]" onClick={onPrev} aria-label="Previous period">‹</button>
        <button className="btn btn-secondary min-h-[44px] px-3 text-xs desktop:min-h-[36px]" onClick={onToday}>Today</button>
        <button className="btn btn-ghost min-h-[44px] w-[44px] p-0 text-lg desktop:min-h-[36px] desktop:w-[36px]" onClick={onNext} aria-label="Next period">›</button>
      </div>
      </div>

      <div>
        <h1 className="m-0 text-[27px] leading-[1.1] desktop:text-[30px]">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-neutral-700">{subtitle}</p>}
      </div>

      {loading && <div role="status" className="calendar-loading-line text-xs text-neutral-600">Loading calendar…</div>}

      {error ? <CalendarLoadError error={error} onRetry={onRetry} /> : (
      <div aria-busy={loading} className={`${loading ? 'opacity-60' : ''} ${hasSidebar ? 'desktop:grid desktop:grid-cols-[minmax(0,1fr)_220px] desktop:gap-5 desktop:items-start' : 'block'}`}>
        <div className="min-w-0">
          {view === 'Agenda' ? <AgendaList events={events} today={clock.dateKey}
              timeZone={timeZone} selectedKey={selectedKey} onSelect={openEvent} />
            : view === 'Month' ? <MonthGrid anchor={selectedDate} events={events}
                today={clock.dateKey} onDateSelect={onDateSelect} onSelect={openEvent} />
              : view === 'Year' ? <YearGrid anchor={selectedDate}
                  today={clock.dateKey} onDateSelect={onDateSelect} />
                : <TimeGrid view={view} anchor={selectedDate} events={events}
                    clock={clock} onSelect={pickEvent} onShowAgenda={() => onViewChange('Agenda')} />}
        </div>

        {hasSidebar && <CalendarSidebar
          anchor={selectedDate}
          today={clock.dateKey}
          selected={selectedEvent}
          timeZone={timeZone}
          onDateSelect={onDateSelect}
          onOpenEvent={openEvent}
        />}
      </div>
      )}

      {!error && <ConnectedCalendars
        connections={connections}
        disclosureStatement={disclosureStatement}
        now={currentTime}
        canManage={canManageConnections}
        onManage={onManageConnections}
      />}
    </div>
  );
}

function CalendarLoadError({ error, onRetry }: {
  error: { message: string; recoverable: boolean }; onRetry?: () => void;
}) {
  return <section role="alert" className="border-y border-neutral-300 py-8 text-center">
    <h2 className="text-base">Calendar could not be loaded</h2>
    <p className="mt-1 text-sm text-neutral-700">{error.message}</p>
    {error.recoverable && onRetry && <button className="btn btn-secondary mt-3 min-h-[40px] px-4 text-sm" onClick={onRetry}>Retry</button>}
  </section>;
}

function ConnectedCalendars({ connections, disclosureStatement, now, canManage, onManage }: {
  connections: CalendarConnectionView[];
  disclosureStatement?: string;
  now: Date;
  canManage: boolean;
  onManage?: () => void;
}) {
  return <section className="pt-1" aria-labelledby="connected-calendars-heading">
    <h6 id="connected-calendars-heading" className="mb-1.5 text-accent-700">Connected calendars</h6>
    {connections.length === 0 ? <div className="flex flex-wrap items-center justify-between gap-2 py-1 text-xs">
      <span className="text-neutral-700">No external calendars are connected.</span>
      {canManage && onManage && <button className="btn btn-secondary min-h-[40px] px-3 text-xs" onClick={onManage}>Calendar settings</button>}
    </div> : <ul className="divide-y divide-neutral-200 border-y border-neutral-200">
      {connections.map((connection) => {
        const content = <>
          <span className="min-w-0">
            <span className="block font-heading font-semibold">{connection.providerLabel}</span>
            {connection.accountLabel && <span className="block truncate text-neutral-700">{connection.accountLabel}</span>}
          </span>
          <span className="text-neutral-700 desktop:text-right">
            <span className="block"><span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${connection.health === 'healthy' ? 'bg-presence-active' : 'bg-presence-idle'}`} aria-hidden />{connection.healthLabel}</span>
            <span className="block">{connection.lastSuccessfulSyncAt
              ? `Synced ${relativeTime(connection.lastSuccessfulSyncAt, now)}` : 'No completed synchronization'}</span>
          </span>
          <span className="text-neutral-700 desktop:text-right">{connection.sharingMode}</span>
        </>;
        return <li key={connection.id}>
          {canManage && onManage ? <button onClick={onManage}
            className="grid min-h-[48px] w-full grid-cols-1 gap-1 px-1 py-2 text-left hover:bg-accent-100 desktop:grid-cols-[minmax(180px,1fr)_auto_auto] desktop:items-center desktop:gap-4"
            aria-label={`Open settings for ${connection.providerLabel}${connection.accountLabel ? `, ${connection.accountLabel}` : ''}`}>
            {content}
          </button> : <div className="grid min-h-[48px] grid-cols-1 gap-1 px-1 py-2 desktop:grid-cols-[minmax(180px,1fr)_auto_auto] desktop:items-center desktop:gap-4">{content}</div>}
        </li>;
      })}
    </ul>}
    {disclosureStatement && <p className="mt-2 text-[11px] leading-snug text-neutral-700">{disclosureStatement}</p>}
  </section>;
}

function relativeTime(value: string, now: Date): string {
  const elapsedSeconds = Math.max(0, Math.floor((now.getTime() - new Date(value).getTime()) / 1000));
  if (elapsedSeconds < 60) return 'just now';
  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

function eventDate(e: CalendarEventView): string { return e.localDate; }
function eventKey(e: CalendarEventView): string { return `${e.id}-${e.teamId ?? ''}`; }
function isoDate(d: Date): string { return d.toISOString().slice(0, 10); }
function utcDate(value: string): Date { return new Date(`${value}T12:00:00Z`); }

export function calendarClockSnapshot(now: Date, timeZone: string): {
  dateKey: string; minute: number; label: string;
} {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00';
  const hour = Number(get('hour'));
  const minute = Number(get('minute'));
  return {
    dateKey: `${get('year')}-${get('month')}-${get('day')}`,
    minute: hour * 60 + minute,
    label: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
  };
}

function useCalendarNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

function eventTimeRange(event: CalendarEventView, timeZone: string): string {
  if (!event.startsAt) return 'All day';
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  const start = formatter.format(new Date(event.startsAt));
  return event.endsAt ? `${start} to ${formatter.format(new Date(event.endsAt))}` : start;
}

function eventDurationLabel(event: CalendarEventView): string {
  const minutes = event.durationMinutes;
  if (minutes == null || minutes <= 0) return '';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${minutes} min`;
  if (remainder === 0) return `${hours} hr`;
  return `${hours} hr ${remainder} min`;
}

function EventCard({ event, onSelect }: {
  event: CalendarEventView; onSelect: (event: CalendarEventView) => void;
}) {
  return <button onClick={() => onSelect(event)}
    title={`${event.title}${event.timeLabel ? ` ${event.timeLabel}` : ''}`}
    className="flex w-full min-w-0 items-center gap-1 rounded-sm border-0 border-l-[3px] border-accent-700 bg-accent-100 px-1 py-0.5 text-left hover:bg-accent-200">
    <span className="min-w-0 flex-1 truncate text-xs font-heading font-semibold">
      {event.title}
    </span>
    {event.timeLabel && <span className="shrink-0 text-[10px] tabular-nums text-neutral-700">{event.timeLabel}</span>}
  </button>;
}

function AgendaList({ events, today, timeZone, selectedKey, onSelect }: {
  events: CalendarEventView[]; today: string; timeZone: string; selectedKey: string | null;
  onSelect: (event: CalendarEventView) => void;
}) {
  const groups = new Map<string, CalendarEventView[]>();
  const sorted = [...events].sort((a, b) => a.localDate.localeCompare(b.localDate)
    || (a.startsAt == null ? -1 : b.startsAt == null ? 1
      : new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
    || a.title.localeCompare(b.title));
  for (const event of sorted) groups.set(eventDate(event), [...(groups.get(eventDate(event)) ?? []), event]);
  if (events.length === 0) return <div className="border-y border-neutral-300 py-10 text-center">
    <p className="font-heading text-sm font-semibold">Nothing is scheduled for this month.</p>
    <p className="mt-1 text-xs text-neutral-600">Trips, visits, and follow-ups will appear here when they are scheduled.</p>
  </div>;
  return <div className="calendar-agenda border-b border-neutral-300">{[...groups].map(([date, items]) => {
    const dateValue = utcDate(date);
    const header = dateValue.toLocaleDateString(undefined, {
      weekday: 'long', month: 'long', day: '2-digit', timeZone: 'UTC',
    });
    return <section key={date}>
      <div className="bg-neutral-200 px-3 py-1.5 text-[11px] text-neutral-700">
        {date === today ? 'Today · ' : ''}{header}
      </div>
      {items.map((event) => {
        const trip = event.kind === 'team_trip';
        const followUp = event.kind === 'follow_up';
        const Icon = trip ? MapIcon : followUp ? PhoneIcon : MapPinIcon;
        const detail = trip
          ? [event.stopCount ? `${event.stopCount} stops` : null,
              event.visitCount ? `${event.visitCount} visits` : null,
              event.roadMiles != null ? `${Math.round(event.roadMiles)} mi` : null,
              eventDurationLabel(event)].filter(Boolean).join(' · ')
          : followUp
            ? [event.disposition, event.note, eventDurationLabel(event)].filter(Boolean).join(' · ')
            : [event.teamName, event.recordCount != null
                ? `${event.recordCount} ${event.recordCount === 1 ? 'record' : 'records'}` : null,
                eventDurationLabel(event)].filter(Boolean).join(' · ');
        const selected = eventKey(event) === selectedKey;
        return <button key={eventKey(event)} onClick={() => onSelect(event)}
          aria-current={selected ? 'true' : undefined}
          className={`grid min-h-[48px] w-full grid-cols-[38px_3px_minmax(0,1fr)] items-center gap-x-2 border-0 border-b border-neutral-300 px-2 py-1.5 text-left hover:bg-accent-100 focus:bg-accent-100 desktop:grid-cols-[38px_3px_minmax(150px,1fr)_auto_auto_auto] desktop:px-2 ${selected ? 'bg-accent-100' : 'bg-transparent'}`}>
          <span className={`row-span-4 flex h-[34px] flex-col items-center justify-center self-start rounded-sm font-heading leading-none desktop:row-span-1 ${date === today ? 'bg-alert text-white' : 'bg-surface text-text'}`}>
            <span className="text-[9px] font-semibold uppercase">{dateValue.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}</span>
            <span className="mt-0.5 text-sm font-semibold">{dateValue.getUTCDate()}</span>
          </span>
          <span className={`row-span-4 h-full min-h-[34px] desktop:row-span-1 ${followUp ? 'bg-alert' : 'bg-accent'}`} aria-hidden />
          <span className="min-w-0 truncate font-heading text-[12px] font-semibold">{event.title}</span>
          <span className="col-start-3 flex min-w-0 items-center gap-1 text-[11px] text-neutral-700 desktop:col-start-auto">
            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="whitespace-nowrap tabular-nums">{eventTimeRange(event, timeZone)}</span>
          </span>
          {detail && <span className="col-start-3 text-[11px] text-neutral-700 desktop:col-start-auto">{detail}</span>}
          <span className={`col-start-3 text-[11px] desktop:col-start-auto ${event.hasConflict ? 'font-semibold text-alert-700' : 'text-neutral-500'}`}>
            {event.conflictLabel}
          </span>
        </button>;
      })}
    </section>;
  })}</div>;
}

function CalendarSidebar({ anchor, today, selected, timeZone, onDateSelect, onOpenEvent }: {
  anchor: string;
  today: string;
  selected: CalendarEventView | null;
  timeZone: string;
  onDateSelect: (date: string, view: CalendarView) => void;
  onOpenEvent: (event: CalendarEventView) => void;
}) {
  const [miniAnchor, setMiniAnchor] = useState(anchor);
  const date = utcDate(miniAnchor);
  const cells = monthCells(miniAnchor);
  const month = date.getUTCMonth();
  return <aside className="hidden desktop:flex min-h-[260px] flex-col gap-5 border-l border-neutral-200 pl-5">
    <div>
      <div className="mb-[6px] flex items-center justify-between">
        <button className="btn btn-ghost min-h-[32px] w-[32px] p-0" aria-label="Previous month"
          onClick={() => { const d = new Date(date); d.setUTCMonth(d.getUTCMonth() - 1); setMiniAnchor(isoDate(d)); }}>‹</button>
        <span className="font-heading text-xs font-semibold">{date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</span>
        <button className="btn btn-ghost min-h-[32px] w-[32px] p-0" aria-label="Next month"
          onClick={() => { const d = new Date(date); d.setUTCMonth(d.getUTCMonth() + 1); setMiniAnchor(isoDate(d)); }}>›</button>
      </div>
      <div className="mb-[2px] grid grid-cols-7 text-center text-[9px] text-neutral-600">
        {['S','M','T','W','T','F','S'].map((label, index) => <span key={`${label}-${index}`}>{label}</span>)}
      </div>
      <div className="grid grid-cols-7 text-center text-[10px]">
        {cells.map((cell) => {
          const key = isoDate(cell);
          const active = key === anchor;
          const isToday = key === today;
          return <button key={key} onClick={() => onDateSelect(key, 'Day')}
            aria-current={isToday ? 'date' : undefined}
            className={`min-h-[24px] rounded-full border-0 font-heading ${cell.getUTCMonth() === month ? 'font-semibold text-text' : 'font-normal text-neutral-400'} ${isToday ? 'bg-alert text-white' : active ? 'bg-neutral-900 text-white' : 'bg-transparent'}`}>
            {cell.getUTCDate()}
          </button>;
        })}
      </div>
    </div>
    <div className="flex flex-1 flex-col gap-2">
      {!selected ? <div className="py-8 text-center text-sm text-neutral-500">No event selected</div> : <>
        <h6 className="m-0 text-accent-700">{selected.kind.replace('_', ' ')}</h6>
        <div className="font-heading text-base font-semibold leading-[1.2]">{selected.title}</div>
        <div className="text-xs">{utcDate(selected.localDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })} · {eventTimeRange(selected, timeZone)}</div>
        <button className="btn btn-primary min-h-[36px] self-start px-3 text-xs"
          onClick={() => onOpenEvent(selected)}>Open event</button>
      </>}
    </div>
  </aside>;
}

function TimeGrid({ view, anchor, events, clock, onSelect, onShowAgenda }: {
  view: 'Day' | 'Week'; anchor: string; events: CalendarEventView[];
  clock: { dateKey: string; minute: number; label: string };
  onSelect: (event: CalendarEventView) => void; onShowAgenda: () => void;
}) {
  const base = utcDate(anchor);
  if (view === 'Week') base.setUTCDate(base.getUTCDate() - base.getUTCDay());
  const days = Array.from({ length: view === 'Day' ? 1 : 7 }, (_, i) => {
    const d = new Date(base); d.setUTCDate(d.getUTCDate() + i); return d;
  });
  const startHour = 6; const endHour = 20;
  const slotsPerHour = 4;
  const totalSlots = (endHour - startHour) * slotsPerHour;
  const columns = view === 'Week' ? 'grid-cols-[48px_repeat(7,minmax(0,1fr))]' : 'grid-cols-[48px_minmax(0,1fr)]';
  const timeLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;
  return <div className="min-w-0 overflow-x-auto">
    <div className={view === 'Week' ? 'min-w-[760px]' : 'min-w-0'}>
      <div className={`grid ${columns}`}>
        <div />{days.map((d) => { const key = isoDate(d); const isToday = key === clock.dateKey; return <button key={key}
          aria-current={isToday ? 'date' : undefined}
          className="flex min-h-[38px] items-center justify-center gap-1 border-0 bg-transparent px-1 py-1 font-heading">
          <span className="text-xs text-neutral-700">{d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })}</span>
          <span className={`inline-flex h-[24px] min-w-[24px] items-center justify-center rounded-full text-[13px] font-semibold ${isToday ? 'bg-alert text-white' : key === anchor ? 'bg-neutral-900 text-white' : ''}`}>{d.getUTCDate()}</span>
        </button>; })}
      </div>
      <div className={`grid ${columns} border-b-2 border-neutral-300`}>
        <div className="pr-1.5 text-right text-[9px] text-neutral-600">all-day</div>
        {days.map((d) => { const key = isoDate(d); const allDay = events.filter((event) => eventDate(event) === key && event.startsAt == null);
          return <div key={key} className="min-h-[24px] space-y-0.5 border-l border-neutral-200 p-0.5">
            {allDay.slice(0, 2).map((event) => <EventCard key={eventKey(event)} event={event} onSelect={onSelect} />)}
            {allDay.length > 2 && <button onClick={onShowAgenda} className="text-[9px] text-accent-700">+{allDay.length - 2}</button>}
          </div>; })}
      </div>
      <div className="max-h-[62vh] overflow-y-auto">
      <div className={`grid relative ${columns}`}>
        <div className="grid h-[784px] grid-rows-[repeat(56,14px)]">
          {Array.from({ length: totalSlots }, (_, slot) => <div key={slot}
            className="flex items-start justify-end pr-1.5 text-[9px] leading-none text-neutral-600 tabular-nums">
            {slot > 0 && slot % slotsPerHour === 0
              ? <span className="-translate-y-1/2 whitespace-nowrap">{timeLabel(startHour + slot / slotsPerHour)}</span>
              : null}
          </div>)}
        </div>
        {days.map((d) => { const key = isoDate(d); const items = events.filter((e) => eventDate(e) === key);
          const timeGroups = new Map<number, CalendarEventView[]>();
          for (const event of items) if (event.startMinute != null) {
            timeGroups.set(event.startMinute, [...(timeGroups.get(event.startMinute) ?? []), event]);
          }
          return <section key={key} className="calendar-hour-grid relative grid h-[784px] grid-cols-4 grid-rows-[repeat(56,14px)] border-l border-neutral-300">
            {key === clock.dateKey && clock.minute >= startHour * 60 && clock.minute < endHour * 60
              ? <CurrentTimeMarker clock={clock} startHour={startHour} /> : null}
            {[...timeGroups].flatMap(([minute, group]) => {
              const visible = group.length > 4 ? group.slice(0, 3) : group;
              const lanes = group.length > 4 ? 4 : group.length;
              const buttons = visible.map((e, lane) => <button key={`${e.id}-${e.teamId ?? ''}`} onClick={() => onSelect(e)}
                className={`z-[2] m-px min-w-0 overflow-hidden rounded-sm border-0 border-l-2 border-accent-700 bg-accent-100 px-1 py-0.5 text-left text-accent-800 hover:bg-accent-200 ${calendarRowStart(minute, startHour)} ${calendarRowSpan(e.durationMinutes ?? 15)} ${calendarLane(lane, lanes)}`}>
                <span className="block truncate text-[10px] font-semibold">{e.title}</span>
                <span className="block truncate text-[9px] opacity-85">{e.timeLabel}</span>
              </button>);
              if (group.length > 4) buttons.push(<button key={`more-${key}-${minute}`} onClick={onShowAgenda}
                className={`z-[3] col-start-4 col-span-1 row-span-3 m-px overflow-hidden rounded border-l-[3px] border-alert bg-neutral-200 px-1 py-1 text-left hover:bg-neutral-300 ${calendarRowStart(minute, startHour)}`}>
                <span className="block text-[10px] font-semibold">+{group.length - 3}</span>
                <span className="block truncate text-[9px]">Agenda</span>
              </button>);
              return buttons;
            })}
          </section>; })}
      </div>
      </div>
    </div>
  </div>;
}

function CurrentTimeMarker({ clock, startHour }: {
  clock: { minute: number; label: string }; startHour: number;
}) {
  const currentRow = Math.max(0, Math.min(839, clock.minute - startHour * 60));
  return <div className="calendar-minute-grid pointer-events-none absolute inset-0 z-[4]" aria-label={`Current time ${clock.label}`}>
    {Array.from({ length: 840 }, (_, minute) => <div key={minute}>
      {minute === currentRow ? <div className="relative border-t border-alert">
        <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-alert" />
      </div> : null}
    </div>)}
  </div>;
}

const CALENDAR_ROW_STARTS = [
  '[grid-row-start:1]', '[grid-row-start:2]', '[grid-row-start:3]', '[grid-row-start:4]',
  '[grid-row-start:5]', '[grid-row-start:6]', '[grid-row-start:7]', '[grid-row-start:8]',
  '[grid-row-start:9]', '[grid-row-start:10]', '[grid-row-start:11]', '[grid-row-start:12]',
  '[grid-row-start:13]', '[grid-row-start:14]', '[grid-row-start:15]', '[grid-row-start:16]',
  '[grid-row-start:17]', '[grid-row-start:18]', '[grid-row-start:19]', '[grid-row-start:20]',
  '[grid-row-start:21]', '[grid-row-start:22]', '[grid-row-start:23]', '[grid-row-start:24]',
  '[grid-row-start:25]', '[grid-row-start:26]', '[grid-row-start:27]', '[grid-row-start:28]',
  '[grid-row-start:29]', '[grid-row-start:30]', '[grid-row-start:31]', '[grid-row-start:32]',
  '[grid-row-start:33]', '[grid-row-start:34]', '[grid-row-start:35]', '[grid-row-start:36]',
  '[grid-row-start:37]', '[grid-row-start:38]', '[grid-row-start:39]', '[grid-row-start:40]',
  '[grid-row-start:41]', '[grid-row-start:42]', '[grid-row-start:43]', '[grid-row-start:44]',
  '[grid-row-start:45]', '[grid-row-start:46]', '[grid-row-start:47]', '[grid-row-start:48]',
  '[grid-row-start:49]', '[grid-row-start:50]', '[grid-row-start:51]', '[grid-row-start:52]',
  '[grid-row-start:53]', '[grid-row-start:54]', '[grid-row-start:55]', '[grid-row-start:56]',
] as const;

const CALENDAR_ROW_SPANS = [
  '', '', '', 'row-span-3', 'row-span-4', 'row-span-5', 'row-span-6',
  'row-span-7', 'row-span-8', 'row-span-9', 'row-span-10', 'row-span-11',
  'row-span-12', '[grid-row-end:span_13]',
] as const;

function calendarRowStart(minute: number, startHour: number): string {
  const slot = Math.max(0, Math.min(CALENDAR_ROW_STARTS.length - 1,
    Math.round((minute - startHour * 60) / 15)));
  return CALENDAR_ROW_STARTS[slot];
}

function calendarRowSpan(durationMinutes: number): string {
  const span = Math.max(3, Math.min(13, Math.round(durationMinutes / 15)));
  return CALENDAR_ROW_SPANS[span];
}

function calendarLane(lane: number, lanes: number): string {
  const layouts = [
    ['col-start-1 col-span-4'],
    ['col-start-1 col-span-2', 'col-start-3 col-span-2'],
    ['col-start-1 col-span-1', 'col-start-2 col-span-1', 'col-start-3 col-span-2'],
    ['col-start-1 col-span-1', 'col-start-2 col-span-1', 'col-start-3 col-span-1', 'col-start-4 col-span-1'],
  ];
  return layouts[Math.max(1, Math.min(4, lanes)) - 1][lane] ?? layouts[0][0];
}

function monthCells(anchor: string): Date[] {
  const first = utcDate(anchor); first.setUTCDate(1);
  const start = new Date(first); start.setUTCDate(1 - first.getUTCDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start); d.setUTCDate(start.getUTCDate() + i); return d;
  });
}

function MonthGrid({ anchor, events, today, onDateSelect, onSelect }: {
  anchor: string; events: CalendarEventView[]; today: string;
  onDateSelect: (date: string, view: CalendarView) => void;
  onSelect: (event: CalendarEventView) => void;
}) {
  const month = utcDate(anchor).getUTCMonth();
  return <div className="min-w-0"><div className="grid grid-cols-7 border-l border-t border-neutral-300">
    {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => <div key={d}
      className="border-b border-r border-neutral-300 p-1 text-center text-[10px] text-neutral-700 desktop:p-1.5 desktop:text-right desktop:text-[11px]">
      <span className="desktop:hidden">{d.slice(0, 1)}</span><span className="hidden desktop:inline">{d}</span>
    </div>)}
    {monthCells(anchor).map((d) => { const key = isoDate(d); const items = events.filter((e) => eventDate(e) === key);
      const isToday = key === today;
      return <div key={key} className={`min-h-[72px] p-0.5 desktop:min-h-[96px] desktop:p-1 border-r border-b border-neutral-300 min-w-0 ${d.getUTCMonth() === month ? 'bg-bg' : 'bg-surface text-neutral-500'}`}>
        <button aria-current={isToday ? 'date' : undefined}
          className={`ml-auto flex h-10 min-w-10 items-center justify-center rounded-full text-[11px] hover:bg-neutral-200 desktop:h-6 desktop:min-w-6 ${isToday ? 'bg-alert text-white hover:bg-alert-700' : ''}`}
          onClick={() => onDateSelect(key, 'Day')}>{d.getUTCDate()}</button>
        <div className="space-y-0.5"><div className="flex gap-0.5 px-1 desktop:hidden">{items.slice(0, 5).map((e) => <span key={`${e.id}-${e.teamId ?? ''}`} className="w-1.5 h-1.5 rounded-full bg-accent" />)}</div>
          <div className="hidden desktop:block space-y-1">{items.slice(0, 3).map((e) => <EventCard key={`${e.id}-${e.teamId ?? ''}`} event={e} onSelect={onSelect} />)}</div>
          {items.length > 3 && <button className="text-[10px] desktop:text-xs text-accent-700" onClick={() => onDateSelect(key, 'Day')}>+{items.length - 3}</button>}
        </div>
      </div>; })}
  </div></div>;
}

function YearGrid({ anchor, today, onDateSelect }: {
  anchor: string; today: string;
  onDateSelect: (date: string, view: CalendarView) => void;
}) {
  const year = utcDate(anchor).getUTCFullYear();
  return <div className="grid grid-cols-1 gap-x-8 gap-y-6 min-[430px]:grid-cols-2 desktop:grid-cols-4">
    {Array.from({ length: 12 }, (_, month) => { const date = new Date(Date.UTC(year, month, 1, 12));
      return <MiniMonth key={month} date={date} today={today} onOpen={() => onDateSelect(isoDate(date), 'Month')} />; })}
  </div>;
}

function MiniMonth({ date, today, onOpen }: { date: Date; today: string; onOpen: () => void }) {
  const anchor = isoDate(date); const month = date.getUTCMonth();
  return <section><button onClick={onOpen} className="mb-1.5 font-heading text-[15px] font-semibold text-accent-700">
    {date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })}</button>
    <div className="mb-1 grid grid-cols-7 text-center text-[9px] text-neutral-600">{['S','M','T','W','T','F','S'].map((d, i) => <span key={`${d}${i}`}>{d}</span>)}</div>
    <div className="grid grid-cols-7 text-center text-[10px]">{monthCells(anchor).map((d) => { const key = isoDate(d); const isToday = key === today; return <button key={key} onClick={() => onOpen()}
      aria-current={isToday ? 'date' : undefined}
      className={`mx-auto flex h-10 min-w-10 items-center justify-center rounded-full desktop:h-6 desktop:min-w-6 ${d.getUTCMonth() === month ? 'text-text' : 'text-neutral-400'} ${isToday ? 'bg-alert text-white' : ''}`}>{d.getUTCDate()}</button>; })}</div>
  </section>;
}

// ---------------------------------------------------------------------------
// More, Profile, Help
// ---------------------------------------------------------------------------

export interface MoreLink {
  id: string;
  label: string;
  description: string;
  group: 'My Account' | 'Support' | 'Management';
  /** Hidden when the signed-in user lacks the permission. §35 */
  requiresPermission?: string;
}

const MORE_ICONS: Record<string, typeof MapIcon> = {
  calendar: CalendarDaysIcon,
  forms: ClipboardDocumentCheckIcon,
  'call-center': PhoneIcon,
  import: ArrowUpTrayIcon,
  'locations-review': MapIcon,
  profile: UserCircleIcon,
  admin: ShieldCheckIcon,
  help: LifebuoyIcon,
  'saved-trips': FolderOpenIcon,
  teams: UsersIcon,
  analysis: ChartBarIcon,
  access: KeyIcon,
  sessions: RectangleGroupIcon,
  notifications: CalendarDaysIcon,
  'sign-out': KeyIcon,
  'call-operations': PhoneIcon,
  'report-problem': LifebuoyIcon,
  'users-access': ShieldCheckIcon,
  'route-settings': MapIcon,
  'audit-history': ChartBarIcon,
};

export function MoreScreen({
  links, heldPermissions, onSelect,
}: {
  links: MoreLink[];
  heldPermissions: ReadonlySet<string>;
  onSelect: (id: string) => void;
}) {
  // Hiding an entry the user cannot use is a UX courtesy, not a control — the
  // server refuses the route regardless. §35
  const visible = links.filter(
    (l) => !l.requiresPermission || heldPermissions.has(l.requiresPermission),
  );
  const groups = (['My Account', 'Support', 'Management'] as const)
    .map((label) => ({ label, items: visible.filter((link) => link.group === label) }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="pt-4 desktop:pt-6 max-w-[560px] flex flex-col gap-6">
      <h1>Menu</h1>
      {groups.map((group) => <section key={group.label}>
        <h6 className="text-accent-700 mb-1">{group.label}</h6>
        <ul>
        {group.items.map((l) => {
          const Icon = MORE_ICONS[l.id] ?? MapIcon;
          return <li key={l.id} className="border-b border-neutral-300">
            <button
              onClick={() => onSelect(l.id)}
              className="w-full py-2 text-left min-h-[48px] hover:bg-neutral-200 transition-colors flex items-center gap-3"
            >
              <Icon className="w-5 h-5 shrink-0 text-accent-700" aria-hidden />
              <span className="flex-1 min-w-0">
                <span className="font-heading text-[17px]">{l.label}</span>
                <span className="ml-1 text-sm text-neutral-700">{l.description}</span>
              </span>
              <ChevronRightIcon className="w-4 h-4 shrink-0 text-neutral-600" aria-hidden />
            </button>
          </li>;
        })}
        </ul>
      </section>)}
    </div>
  );
}

export interface ProfileData {
  displayName: string;
  email: string | null;
  roles: string[];
  scopeLabel: string;
  mfaMethods: Array<{ id: string; label: string; addedLabel: string }>;
  sessions: Array<{ id: string; label: string; current: boolean }>;
  recoveryCodesRemaining: number;
}

export function ProfileScreen({
  data, onAddSecurityKey, onRegenerateCodes, onRevokeSession, onSignOutEverywhere,
}: {
  data: ProfileData | null;
  onAddSecurityKey: () => void;
  onRegenerateCodes: () => void;
  onRevokeSession: (id: string) => void;
  onSignOutEverywhere: () => void;
}) {
  const [tab, setTab] = useState<'profile' | 'security' | 'access' | 'sessions'>('profile');
  if (!data) return <p className="pt-8 text-base text-neutral-700">Loading…</p>;

  const tabs = [
    ['profile', 'Profile'], ['security', 'Security'],
    ['access', 'Access'], ['sessions', 'Sessions'],
  ] as const;

  return (
    <div className="pt-4 max-w-[860px]">
      <header className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-accent-100 text-accent-800 flex items-center justify-center font-heading font-semibold text-h4">
          {data.displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <h1 className="mb-0 truncate">{data.displayName}</h1>
          <p className="text-base text-neutral-700 truncate">
            {data.email ?? 'No email on file'} · {data.roles.join(', ') || 'No role assigned'}
          </p>
        </div>
      </header>

      <div role="tablist" aria-label="Profile" className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-none">
        {tabs.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id}
            className={`seg shrink-0 ${tab === id ? 'seg-active' : ''}`}
            onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'profile' && (
        <section className="grid gap-3 desktop:grid-cols-2">
          <div className="card"><p className="card-kicker">Display name</p><p className="card-title">{data.displayName}</p></div>
          <div className="card"><p className="card-kicker">Email</p><p className="card-title break-all">{data.email ?? 'No email on file'}</p></div>
          <p className="text-sm text-neutral-700 desktop:col-span-2">Contact an administrator to change identity fields.</p>
        </section>
      )}

      {tab === 'security' && (
        <section>
          <h6 className="text-accent-700 mb-2">Sign-in methods</h6>
          <ul className="grid gap-2 mb-4">
            {data.mfaMethods.map((m) => (
              <li key={m.id} className="card flex items-center gap-3">
                <KeyIcon className="w-6 h-6 text-accent-700 shrink-0" aria-hidden />
                <span className="flex-1 min-w-0">
                  <span className="block font-heading font-semibold text-base">{m.label}</span>
                  <span className="block text-sm text-neutral-700">Added {m.addedLabel}</span>
                </span>
                <span className="tag tag-neutral">Active</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col desktop:flex-row gap-2">
            <button className="btn btn-primary" onClick={onAddSecurityKey}>Add YubiKey or security key</button>
            <button className="btn btn-secondary" onClick={onRegenerateCodes}>Generate new recovery codes</button>
          </div>
          <p className="text-sm text-neutral-700 mt-2">
            {data.recoveryCodesRemaining} recovery {data.recoveryCodesRemaining === 1 ? 'code' : 'codes'} remaining. Generating a new set replaces unused codes.
          </p>
        </section>
      )}

      {tab === 'access' && (
        <section className="card">
          <h6 className="text-accent-700 mb-3">Access</h6>
          <dl className="grid gap-3 desktop:grid-cols-2">
            <div><dt className="text-sm text-neutral-700">Role</dt><dd className="font-heading font-semibold text-h4">{data.roles.join(', ') || 'No role assigned'}</dd></div>
            <div><dt className="text-sm text-neutral-700">Data scope</dt><dd className="font-heading font-semibold text-h4">{data.scopeLabel}</dd></div>
          </dl>
          <p className="text-sm text-neutral-700 mt-4">Administrators manage role and scope assignments.</p>
        </section>
      )}

      {tab === 'sessions' && (
        <section>
          <ul className="grid gap-2 mb-4">
            {data.sessions.map((s, index) => (
              <li key={s.id} className="card flex items-center gap-3">
                {index === 0 ? <DevicePhoneMobileIcon className="w-6 h-6 text-accent-700 shrink-0" aria-hidden /> : <ComputerDesktopIcon className="w-6 h-6 text-accent-700 shrink-0" aria-hidden />}
                <span className="flex-1 text-base min-w-0">{s.label}{s.current && <span className="block text-sm text-accent-700">This device</span>}</span>
                {!s.current && <button className="btn btn-ghost" onClick={() => onRevokeSession(s.id)}>End session</button>}
              </li>
            ))}
          </ul>
          <button className="btn btn-secondary" onClick={onSignOutEverywhere}>Sign out other sessions</button>
        </section>
      )}
    </div>
  );
}

export interface HelpTopic {
  id: string;
  question: string;
  answer: string;
}

export function HelpScreen({ topics, supportLine }: {
  topics: HelpTopic[]; supportLine: string;
}) {
  return (
    <div className="pt-4">
      <h1 className="mb-4">Help</h1>
      <dl>
        {topics.map((t) => (
          <div key={t.id} className="border-b border-neutral-300 py-4">
            <dt className="font-heading font-semibold text-h4 mb-1">{t.question}</dt>
            <dd className="text-base">{t.answer}</dd>
          </div>
        ))}
      </dl>
      <p className="text-base text-neutral-700 mt-6">{supportLine}</p>
    </div>
  );
}
